/**
 * Static list of all Core weather data fields from the Tomorrow.io API.
 * Includes suffix variants (Min, Max, Avg) where supported per field availability.
 * Reference: https://docs.tomorrow.io/reference/weather-data-layers
 * Reference: https://docs.tomorrow.io/reference/data-layers-core
 */

export interface WeatherField {
  name: string;
  label: string;
  unit: { metric: string; imperial: string };
  /** If true, this field is not numeric and should be excluded from charting */
  nonNumeric?: boolean;
}

/** Builds base + Min/Max/Avg variant entries for a field */
const withSuffixes = (
  base: Omit<WeatherField, 'label'> & { label: string },
  suffixes: Array<{ suffix: 'Min' | 'Max' | 'Avg' }>,
): WeatherField[] => [
  { ...base, name: base.name, label: base.label },
  ...suffixes.map(({ suffix }) => ({
    ...base,
    name: `${base.name}${suffix}` as string,
    label: `${base.label} (${suffix})`,
  })),
];

/** Builds only Min/Max variant entries (no base, no Avg) - for fields like weatherCode */
const suffixMinMaxOnly = (
  base: Omit<WeatherField, 'label'> & { label: string },
): WeatherField[] => [
  { ...base, name: `${base.name}Min`, label: `${base.label} (Min)` },
  { ...base, name: `${base.name}Max`, label: `${base.label} (Max)` },
];

export const CORE_WEATHER_FIELDS: WeatherField[] = [
  // Temperature (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'temperature', label: 'Temperature', unit: { metric: '°C', imperial: '°F' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'temperatureApparent', label: 'Apparent Temperature', unit: { metric: '°C', imperial: '°F' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'dewPoint', label: 'Dew Point', unit: { metric: '°C', imperial: '°F' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Humidity (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'humidity', label: 'Humidity', unit: { metric: '%', imperial: '%' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Wind (windSpeed, windGust: S: ∧ ∨ ~ ⧖; windDirection: no suffix)
  ...withSuffixes(
    { name: 'windSpeed', label: 'Wind Speed', unit: { metric: 'm/s', imperial: 'mph' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  { name: 'windDirection', label: 'Wind Direction', unit: { metric: '°', imperial: '°' } },
  ...withSuffixes(
    { name: 'windGust', label: 'Wind Gust', unit: { metric: 'm/s', imperial: 'mph' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Pressure (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'pressureSurfaceLevel', label: 'Pressure (Surface Level)', unit: { metric: 'hPa', imperial: 'inHg' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'pressureSeaLevel', label: 'Pressure (Sea Level)', unit: { metric: 'hPa', imperial: 'inHg' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Precipitation Intensity (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'precipitationIntensity', label: 'Precipitation Intensity', unit: { metric: 'mm/hr', imperial: 'in/hr' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'rainIntensity', label: 'Rain Intensity', unit: { metric: 'mm/hr', imperial: 'in/hr' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'freezingRainIntensity', label: 'Freezing Rain Intensity', unit: { metric: 'mm/hr', imperial: 'in/hr' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'snowIntensity', label: 'Snow Intensity', unit: { metric: 'mm/hr', imperial: 'in/hr' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'sleetIntensity', label: 'Sleet Intensity', unit: { metric: 'mm/hr', imperial: 'in/hr' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Precipitation Probability & Type (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'precipitationProbability', label: 'Precipitation Probability', unit: { metric: '%', imperial: '%' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'precipitationType', label: 'Precipitation Type', unit: { metric: 'code', imperial: 'code' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Accumulation (S: ∧ ∨ ~ ⧖ or ∧ ∨ ~ for LWE variants)
  ...withSuffixes(
    { name: 'rainAccumulation', label: 'Rain Accumulation', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'snowAccumulation', label: 'Snow Accumulation', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'snowAccumulationLwe', label: 'Snow Accumulation (LWE)', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'snowDepth', label: 'Snow Depth', unit: { metric: 'cm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'sleetAccumulation', label: 'Sleet Accumulation', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'sleetAccumulationLwe', label: 'Sleet Accumulation (LWE)', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'iceAccumulation', label: 'Ice Accumulation', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'iceAccumulationLwe', label: 'Ice Accumulation (LWE)', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Solar (no suffix)
  { name: 'sunriseTime', label: 'Sunrise Time', unit: { metric: 'ISO-8601', imperial: 'ISO-8601' }, nonNumeric: true },
  { name: 'sunsetTime', label: 'Sunset Time', unit: { metric: 'ISO-8601', imperial: 'ISO-8601' }, nonNumeric: true },

  // Visibility (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'visibility', label: 'Visibility', unit: { metric: 'km', imperial: 'mi' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Cloud (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'cloudCover', label: 'Cloud Cover', unit: { metric: '%', imperial: '%' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'cloudBase', label: 'Cloud Base', unit: { metric: 'km', imperial: 'mi' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'cloudCeiling', label: 'Cloud Ceiling', unit: { metric: 'km', imperial: 'mi' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Moon (no suffix)
  { name: 'moonPhase', label: 'Moon Phase', unit: { metric: 'code', imperial: 'code' } },

  // UV (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'uvIndex', label: 'UV Index', unit: { metric: 'index', imperial: 'index' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
  ...withSuffixes(
    { name: 'uvHealthConcern', label: 'UV Health Concern', unit: { metric: 'index', imperial: 'index' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Agriculture - Growing Degree Days (no suffix in docs)
  { name: 'gdd10To30', label: 'Growing Degree Days (10-30°C)', unit: { metric: 'GDD', imperial: 'GDD' } },
  { name: 'gdd10To31', label: 'Growing Degree Days (10-31°C)', unit: { metric: 'GDD', imperial: 'GDD' } },
  { name: 'gdd08To30', label: 'Growing Degree Days (08-30°C)', unit: { metric: 'GDD', imperial: 'GDD' } },
  { name: 'gdd03To25', label: 'Growing Degree Days (03-25°C)', unit: { metric: 'GDD', imperial: 'GDD' } },

  // Evapotranspiration (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'evapotranspiration', label: 'Evapotranspiration', unit: { metric: 'mm', imperial: 'in' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Weather Codes (S: ∧ ∨ ⧖ - Min/Max only, no Avg)
  { name: 'weatherCode', label: 'Weather Code', unit: { metric: 'code', imperial: 'code' } },
  ...suffixMinMaxOnly({ name: 'weatherCode', label: 'Weather Code', unit: { metric: 'code', imperial: 'code' } }),
  { name: 'weatherCodeFullDay', label: 'Weather Code (Full Day)', unit: { metric: 'code', imperial: 'code' } },
  ...suffixMinMaxOnly({ name: 'weatherCodeFullDay', label: 'Weather Code (Full Day)', unit: { metric: 'code', imperial: 'code' } }),
  { name: 'weatherCodeDay', label: 'Weather Code (Day)', unit: { metric: 'code', imperial: 'code' } },
  ...suffixMinMaxOnly({ name: 'weatherCodeDay', label: 'Weather Code (Day)', unit: { metric: 'code', imperial: 'code' } }),
  { name: 'weatherCodeNight', label: 'Weather Code (Night)', unit: { metric: 'code', imperial: 'code' } },
  ...suffixMinMaxOnly({ name: 'weatherCodeNight', label: 'Weather Code (Night)', unit: { metric: 'code', imperial: 'code' } }),

  // Thunderstorm (S: ∧ ∨ ~)
  ...withSuffixes(
    { name: 'thunderstormProbability', label: 'Thunderstorm Probability', unit: { metric: '%', imperial: '%' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Heat Stress (S: ∧ ∨ ~ ⧖)
  ...withSuffixes(
    { name: 'heatIndex', label: 'Heat Index', unit: { metric: 'index', imperial: 'index' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),

  // Wet Bulb Globe Temperature (S: ∧ ∨ ~)
  ...withSuffixes(
    { name: 'wetBulbGlobeTemperature', label: 'Wet Bulb Globe Temperature', unit: { metric: '°C', imperial: '°F' } },
    [{ suffix: 'Min' }, { suffix: 'Max' }, { suffix: 'Avg' }],
  ),
];

/** Fields suitable for charting (numeric only) */
export const CHARTABLE_FIELDS = CORE_WEATHER_FIELDS.filter((f) => !f.nonNumeric);

/** Color palette for chart lines */
export const CHART_COLORS = [
  '#1890ff', '#52c41a', '#fa8c16', '#f5222d', '#722ed1',
  '#13c2c2', '#eb2f96', '#faad14', '#2f54eb', '#a0d911',
  '#fa541c', '#1677ff', '#36cfc9', '#ff85c0', '#ffc53d',
];
